/**
 * Pure decision logic for the router-wide error boundary (see `route-error.tsx`).
 *
 * Kept in its own dependency-free module so the branch logic is unit-testable without pulling in
 * `./main` (the tRPC client / persister, whose import isn't node-safe) or a router/DOM. The effect
 * and fallback UI this drives are covered by the redirect e2e tests.
 */

// The route-policy prefix sets are the SAME ones the proactive `_app` layout guard uses (route-access) —
// shared so the reactive boundary net and the guard can never drift apart.
import { AUTH_REQUIRED_ROUTE_PREFIXES, TEST_GATED_ROUTE_PREFIXES } from './route-access'

// The cached `getUser` shape the classifier reads (authOptional → null when logged out).
export interface RouteErrorUser {
  type?: string
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

const CHUNK_LOAD_MESSAGE =
  /dynamically imported module|imported module script failed|importing a module script failed|ChunkLoadError|Loading chunk/i

/**
 * Depth of the `cause` chain {@link isChunkLoadError} walks. Three covers the wrappers seen in practice
 * and keeps a cyclic or absurdly deep chain from costing anything.
 */
const MAX_CAUSE_DEPTH = 3

/**
 * Did a CODE chunk fail to load (a dynamic `import()` of a route/lazy component)?
 *
 * This needs its own branch because the browser CACHES the rejection: re-importing the same URL returns the
 * cached failure, so re-rendering or refetching can never recover it — only a full reload re-fetches. And
 * because it's usually not a bug: hashed assets persist on the CDN (upload-to-cdn.ts), so the common cause is
 * the network being down, which is why the boundary waits for reachability instead of reloading blind.
 *
 * Matches the dynamic-import wordings ONLY, and deliberately NOT a bare "Failed to fetch" — that is also what a
 * failed tRPC DATA query throws offline, and a data error must recover in place, not by reloading the document.
 * Each engine words it differently: Chrome "Failed to fetch dynamically imported module", Safari "Importing a
 * module script failed", Firefox "error loading dynamically imported module"; bundlers add ChunkLoadError.
 */
export function isChunkLoadError(error: unknown): boolean {
  // Down the `cause` chain, not just the top error. A failed import does not always reach us as itself:
  // when it happens during concurrent rendering React recovers on its own, and what surfaces is a
  // generic `Minified React error #…` with the real failure demoted to
  // `cause: TypeError: Failed to fetch dynamically imported module: …`. Matching only the top message
  // read that as a React fault — so the boundary showed a hard error instead of waiting for the network,
  // and `logFrontendError` ticketed a deploy as a bug.
  let current: unknown = error
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    const message = current instanceof Error ? current.message : typeof current === 'string' ? current : ''
    if (CHUNK_LOAD_MESSAGE.test(message)) return true
    if (!(current instanceof Error)) return false
    current = current.cause
    if (current == null) return false
  }
  return false
}

/**
 * Given the active route, the cached user and the thrown message, decide whether the boundary should
 * redirect to login (`isAuth`) or to the unfinished test (`isTestIncomplete`).
 *
 *  - No cached user on an auth-required route → auth/expired (key off the absent session, NOT the error
 *    code: a DEFERRED-streamed query's rejection has its code REDACTED in production).
 *  - A logged-in applicant with the test still pending, on a test-gated route → resume the test (same
 *    reason: the streamed message is often redacted). An explicit TEST_INCOMPLETE message also counts.
 */
export function classifyRouteError(input: {
  routeId: string
  cachedUser: RouteErrorUser | null
  message?: string | undefined
}): { isAuth: boolean; isTestIncomplete: boolean } {
  const { routeId, cachedUser, message } = input
  const hasUser = cachedUser != null
  const needsAuth = AUTH_REQUIRED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isAuth = !hasUser && needsAuth
  const applicantTestPending =
    cachedUser?.type === 'applicant' &&
    !(cachedUser.testPassed && cachedUser.testPassedValues && cachedUser.testPassedPersonality)
  const onTestGatedRoute = TEST_GATED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isTestIncomplete = message === 'TEST_INCOMPLETE' || (applicantTestPending && onTestGatedRoute)
  return { isAuth, isTestIncomplete }
}
